import React, { useState } from "react";
import { LightbulbIcon, X } from "lucide-react";
import BookIcon from "src/assets/images/book.svg";
import SwitchIcon from "src/assets/images/switch.svg";
import { Modal } from "src/components/reusable/modal/Modal";

interface FieldDetail {
    coverage: string;
    data_type: string;
    unique_values: number;
    outliers: number;
    outlier_examples: (string | number)[];
}

interface DatasetDetail {
    dataset: string;
    coverage: string;
    outliers: string;
    notes: string;
    filename: string;
    field_details: Record<string, FieldDetail>;
}

interface SchemaValidationProps {
    json_response: DatasetDetail[] | null;
}

const SchemaValidation: React.FC<SchemaValidationProps> = ({ json_response }) => {
    const [open, setOpen] = useState(true);
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});
    const [notesOpen, setNotesOpen] = useState(false);
    const [activeNotes, setActiveNotes] = useState<string>("");

    if (!json_response || json_response.length === 0) {
        return <div>No validation results available.</div>;
    }

    const toggleExpand = (idx: number) => {
        setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
    };

    return (
        <div
            className={`h-screen rounded-lg w-full overflow-hidden transition-all duration-300 ease-in-out ${open
                ? "flex-[0_0_100%] sm:flex-[0_0_75%] lg:flex-[0_0_600px] max-w-full lg:max-w-[600px]"
                : "flex-[0_0_100%] sm:flex-[0_0_215px] max-w-full sm:max-w-[215px]"
                }`}
        >
            <div className="flex flex-col h-full bg-opacity-50">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <img src={BookIcon} alt="Book Icon" className="w-3 h-3 object-contain" />
                        <h2 className="text-lg font-semibold">Schema Validation Results</h2>
                    </div>
                    <button
                        onClick={() => setOpen(!open)}
                        className="cursor-pointer rounded border border-[#DFE3E8] p-1 hover:bg-gray-100"
                        aria-label="Toggle Panel"
                    >
                        <img src={SwitchIcon} alt="Toggle" className="w-3 h-3 object-contain" />
                    </button>
                </div>

                {/* Main Content */}
                {open && (
                    <div className="h-[55vh] overflow-y-auto pn_scroller flex flex-col">
                        <div className="overflow-x-auto">
                            <table className="w-full border border-gray-200 rounded-lg">
                                <thead>
                                    <tr className="bg-gray-100 text-left">
                                        <th className="p-3 border-b">Dataset</th>
                                        <th className="p-3 border-b">Coverage</th>
                                        <th className="p-3 border-b">Outliers</th>
                                        <th className="p-3 border-b">Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {json_response.map((row, i) => (
                                        <>
                                            <tr key={i} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 border-none">{row.dataset}</td>
                                                <td className="px-3 py-2 border-none">{row.coverage}</td>
                                                <td className="px-3 py-2 border-none">{row.outliers}</td>
                                                <td className="px-3 py-2 border-none">
                                                    <button
                                                        className="text-primary-900 underline font-medium text-sm cursor-pointer"
                                                        onClick={() => { setActiveNotes(row.notes); setNotesOpen(true); }}
                                                    >
                                                        View notes
                                                    </button>
                                                </td>
                                            </tr>

                                        </>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Bottom Note */}
                {open && (
                    <div className="flex items-start gap-2 text-xs text-gray-600 mt-4 px-1">
                        <LightbulbIcon className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span>
                            Please review the schema diagnostics carefully. Confirm correctness or request
                            adjustments before proceeding.
                        </span>
                    </div>
                )}

                {/* Footer Actions */}
                {/* {open && (
                    <div className="mt-4 pt-3 border-t border-[#DBDEEB] flex flex-col items-end">
                        <div className="flex items-center gap-2 mb-2">
                            <button className="cursor-pointer rounded-lg px-2 py-1 border border-secondary-500 hover:bg-foundationOrange-100 text-[#373D3F]">
                                Edit
                            </button>
                            <button className="cursor-pointer rounded-lg px-2 py-1 border border-secondary-500 hover:bg-foundationOrange-100 text-[#373D3F]">
                                Replace/Remove
                            </button>
                        </div>
                        <button className="cursor-pointer rounded-lg px-2 py-1 border bg-secondary-500 text-[#373D3F]">
                            Accept & Continue
                        </button>
                    </div>
                )} */}
            </div>
            <Modal open={notesOpen} handleOnClose={() => setNotesOpen(false)}>
                <div className="p-6 bg-white rounded-lg max-w-lg mx-auto text-left">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold">Notes</h3>
                        <X className="z-10 cursor-pointer" onClick={() => setNotesOpen(false)} />
                    </div>
                    <div className="text-sm whitespace-pre-line">{activeNotes}</div>
                </div>
            </Modal>
        </div>
    );
};

export default SchemaValidation;
